<template>
  <cs-form-dialog
    v-model="popupDialog"
    max-width="750px"
    :id="`publish-draft-dialog-${moduleId}`"
    heading="Publish Content"
    :primary-action="{
      label: 'Update',
      eventName: 'dlg-ok'
    }"
    :secondary-action1="{ label: 'Cancel', eventName: 'dlg-cancel' }"
    @primary-click="publishDraft"
    @secondary1-click="cancelDialog"
  >
    <template v-slot:cs-form-dialog-content>
      <v-form>
        <v-container fluid="fluid">
          <v-row>
            <v-col cols="12">
              <c-e-content-editor-tip-tap
                id="publish-cgc-dialog--release-notes-editor"
                :showDialog="popupDialog"
                v-model="editorTextContent"
              ></c-e-content-editor-tip-tap>
              <div v-if="requiredError" class="text-error">A summary is required</div>
            </v-col>
          </v-row>
        </v-container>
      </v-form>
    </template>
  </cs-form-dialog>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex';
import TurndownService from 'turndown';
import { CSBase, CSFormDialog } from '@complispace/cs-design-system';
import CEContentEditorTipTap from '@/components/content/CEContentEditorTipTap';
import { publish } from '@/api';
import {
  KEEP_MARKDOWN_TAGS,
  MESSAGES,
  MIXPANEL_TRACE_EVENT,
  MIXPANEL_TRACE_STRING
} from '@/constants';
import * as types from '@/store/mutationTypes';
import dataManager from '@/lib/engine';
import plugInAdapter from '@/lib/plugInAdapter';

export default {
  name: 'PublishCGCDialog',

  extends: CSBase,

  components: {
    CEContentEditorTipTap,
    CSFormDialog
  },

  data() {
    return {
      popupDialog: false,
      editorTextContent: '',
      requiredError: false
    };
  },

  props: {
    showDialog: {
      type: Boolean,
      required: true,
      default: false
    },

    moduleId: {
      type: Number
    },

    moduleChildActivated: {
      type: Boolean,
      default: false
    },

    moduleToc: {
      type: Object,
      required: false
    },

    isDraftModeEnabled: {
      type: Boolean,
      default: false
    }
  },

  emits: ['closed', 'published'],

  watch: {
    showDialog(value) {
      if (value) {
        this.popupDialog = true;
      }
    },

    popupDialog(value) {
      if (!value) {
        this.editorTextContent = '';
        this.requiredError = false;
        this.$emit('closed');
      }
    },

    editorTextContent() {
      this.requiredError = false;
    }
  },

  computed: {
    ...mapState({
      subdomain: (state) => state.session.subdomain,
      client: (state) => state.client
    }),

    ...mapGetters({
      activatedCEModules: 'activatedCEModules/items',
      isClientView: 'authorisation/isClientView',
      appName: 'app/name'
    })
  },

  methods: {
    ...mapMutations({
      setDataUpdatedModulesState: types.DATA_SET_UNPUBLISHED_UPDATES_STATE
    }),

    getActivatedCEModule(moduleId) {
      if (this.activatedCEModules && this.activatedCEModules.length) {
        return this.activatedCEModules.find((module) => module.id === moduleId);
      }
      return null;
    },

    cancelDialog() {
      this.popupDialog = false;
    },

    async publishDraft() {
      try {
        const turndownService = new TurndownService();
        // Keep below HTML tags in markdown.
        turndownService.keep(KEEP_MARKDOWN_TAGS);
        const mdContent = turndownService.turndown(this.editorTextContent);

        if (mdContent.trim()) {
          this.setLoading(true);

          if (this.moduleToc) {
            // For page reordering, pass updated TOC to backend
            await publish.publishDraft(this.moduleId, mdContent, this.moduleToc);
          } else {
            const response = await publish.publishDraft(
              this.moduleId,
              mdContent,
              undefined,
              true,
              this.isDraftModeEnabled
            );
            if (response.status === 201 && response.data && response.data.eventTrackingData) {
              this.trackUGCPublishEvent(response.data.eventTrackingData);
            }
          }

          this.setDataUpdatedModulesState(dataManager.MANAGER_STATE.STATE_IDLE);
          this.$emit('published', this.moduleId);

          this.popupDialog = false;

          this.setLoading(false);
        } else {
          this.requiredError = true;
        }
      } catch (err) {
        if (
          err.response &&
          err.response.data &&
          err.response.data.message === 'OperationNotAllowed'
        ) {
          // If page reordering can't be done as there have been updates made to the master branch TOC,
          // throw an error to refresh the page before reordering again.
          this.showWarningAlert(MESSAGES.ERROR.REORDERING_PUBLISH_ERROR, true);
        } else {
          this.showErrorAlert(err.message, true);
        }

        this.setLoading(false);
      }
    },

    trackUGCPublishEvent(eventTrackingData) {
      try {
        const { plugIns } = this.$app;

        const userRole = this.isClientView
          ? MIXPANEL_TRACE_STRING.USER_ROLE_VIEW.CLIENT
          : MIXPANEL_TRACE_STRING.USER_ROLE_VIEW.ADMIN;
        const currentModule = this.getActivatedCEModule(this.moduleId);
        let moduleOwnerId;
        let moduleName;
        if (currentModule) {
          moduleOwnerId = currentModule.clientId;
          moduleName = currentModule.displayName;
        }

        const commonMixpanelProperties = {
          appName: this.appName,
          client: this.client,
          subdomain: this.subdomain,
          userRole,
          moduleId: this.moduleId,
          moduleOwnerId,
          moduleName
        };

        if (eventTrackingData.isFirstPublish) {
          // Track UGC Module Creation event in Mixpanel.
          const properties =
            plugInAdapter.Adapter.getCustomMixpanelProperties(commonMixpanelProperties);

          plugIns.mixPanelTrackEvent(MIXPANEL_TRACE_EVENT.UGC_MODULE_CREATION, properties);
        }

        if (eventTrackingData.pageDifferences) {
          // Track UGC Page Creation, UGC Page Updation and UGC Page Deletion events in Mixpanel.
          const { added, modified, deleted } = eventTrackingData.pageDifferences;

          if (added && added.length) {
            added.forEach((addedPage) => {
              const properties = plugInAdapter.Adapter.getCustomMixpanelProperties({
                ...commonMixpanelProperties,
                pagePath: addedPage.fileName,
                pageTitle: addedPage.pageName
              });

              plugIns.mixPanelTrackEvent(MIXPANEL_TRACE_EVENT.UGC_PAGE_CREATION, properties);
            });
          }

          if (modified && modified.length) {
            modified.forEach((modifiedPage) => {
              const properties = plugInAdapter.Adapter.getCustomMixpanelProperties({
                ...commonMixpanelProperties,
                pagePath: modifiedPage.fileName,
                pageTitle: modifiedPage.pageName
              });

              plugIns.mixPanelTrackEvent(MIXPANEL_TRACE_EVENT.UGC_PAGE_UPDATE, properties);
            });
          }

          if (deleted && deleted.length) {
            deleted.forEach((deletedPage) => {
              const properties = plugInAdapter.Adapter.getCustomMixpanelProperties({
                ...commonMixpanelProperties,
                pagePath: deletedPage.fileName,
                pageTitle: deletedPage.pageName
              });

              plugIns.mixPanelTrackEvent(MIXPANEL_TRACE_EVENT.UGC_PAGE_DELETION, properties);
            });
          }
        }
      } catch (error) {
        console.log('Mixpanel Error: ', error);
      }
    }
  }
};
</script>
